import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, Subscription } from 'rxjs';
import {
  PaginatedApiResponse,
  PaginationMeta,
} from '../../../core/models/pagination.model';
import { Directive } from '@angular/core';

@Directive({
  selector: '[appInfiniteListItem]',
  standalone: true,
})
export class InfiniteListItemDirective {}

@Component({
  selector: 'app-infinite-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './infinite-list.component.html',
  styleUrl: './infinite-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfiniteListComponent<T = any>
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  @Input({ required: true })
  fetchPage!: (
    page: number,
    params?: any,
  ) => Observable<PaginatedApiResponse<T[]>>;

  @Input() params?: Record<string, any>;

  @Input() items: T[] | null = null;

  @Input() initialPage: number = 1;

  @Input() initialHasNext: boolean | null = null;

  @Input() trackByFn?: (index: number, item: T) => any;

  @Input() sortFn?: (a: T, b: T) => number;

  @Input() itemsContainerClass = '';

  @Input() root: HTMLElement | null = null;

  @Output() itemsChange = new EventEmitter<T[]>();

  @ViewChild('infiniteAnchor', { static: false })
  infiniteAnchor?: ElementRef<HTMLDivElement>;

  @ContentChild(InfiniteListItemDirective, { read: TemplateRef })
  itemTemplate: TemplateRef<any> | null = null;

  private internalItems: T[] = [];
  private currentPage = 0;
  private hasNext = true;
  isLoading = signal(false);

  private observer?: IntersectionObserver;
  private pageSub?: Subscription;

  constructor(private host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    if (this.items && this.items.length > 0) {
      this.internalItems = [...this.items];
      this.currentPage = this.initialPage || 1;
      this.hasNext =
        this.initialHasNext !== null && this.initialHasNext !== undefined
          ? this.initialHasNext
          : true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] && this.items) {
      // When parent updates the items array (e.g. after deletion),
      // keep internal state in sync.
      this.internalItems = [...this.items];
    }

    if (changes['params'] && !changes['params'].firstChange) {
      // When filters/params change, reset and reload from the initial page.
      this.resetAndReload();
    }
  }

  ngAfterViewInit(): void {
    if (!this.infiniteAnchor) {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.onIntersection();
          }
        });
      },
      {
        root: this.root,
        threshold: 0.1,
      },
    );

    this.observer.observe(this.infiniteAnchor.nativeElement);
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = undefined;
    }

    if (this.pageSub) {
      this.pageSub.unsubscribe();
      this.pageSub = undefined;
    }
  }

  get viewItems(): T[] {
    const source =
      this.items && this.items.length > 0 ? this.items : this.internalItems;

    if (!this.sortFn) {
      return source;
    }

    return [...source].sort(this.sortFn);
  }

  defaultTrackBy(index: number, item: T): any {
    return index;
  }

  private onIntersection(): void {
    if (this.isLoading() || !this.hasNext) {
      return;
    }

    this.loadPage(this.currentPage + 1);
  }

  private resetAndReload(): void {
    this.internalItems = [];
    this.currentPage = 0;
    this.hasNext = true;
    this.emitItemsChange();
    this.loadPage(this.initialPage || 1);
  }

  private loadPage(page: number): void {
    if (!this.fetchPage || this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    if (this.pageSub) {
      this.pageSub.unsubscribe();
    }

    this.pageSub = this.fetchPage(page, this.params).subscribe({
      next: (response) => {
        const newItems = response.results || [];

        if (page <= this.currentPage) {
          // Avoid re-appending data for pages that are already loaded.
          this.isLoading.set(false);
          return;
        }

        this.internalItems = [...this.internalItems, ...newItems];
        this.currentPage = page;

        this.hasNext = Boolean(response.next);

        this.emitItemsChange();
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  private emitItemsChange(): void {
    this.itemsChange.emit([...this.internalItems]);
  }
}
